import { supabase, supabaseRpc } from '@/lib/supabase'
import type { Reward } from '@/models/supabase/Reward'
import type { CollectedRewardRow, RewardRow } from '@/models/supabase/Reward'
import { groupRewardsByChild } from '@/services/rewardAssignment'
import { useAnalytics } from '@/composables/useAnalytics'

const analytics = useAnalytics()

export const supabaseRewardService = {
  // Get all rewards for a household using RPC function
  async getHouseholdRewards(householdId: string): Promise<Reward[]> {
    const { data, error } = await supabaseRpc
      .rpc('get_household_rewards_safe', {
        p_household_id: householdId
      })

    if (error) throw error

    // The RPC function returns JSONB objects with category as text, so we can use them directly
    return (data || []) as Reward[]
  },

  // Get rewards for a specific child (filtered by assignments)
  async getRewardsForChild(childId: string): Promise<Reward[]> {
    const { data, error } = await supabaseRpc
      .rpc('get_rewards_for_child', {
        p_child_id: childId
      })

    if (error) throw error

    return (data || []) as Reward[]
  },

  // Batch version of getRewardsForChild: replaces N per-child `get_rewards_for_child`
  // RPCs with TWO queries — one for all household rewards, one for all assignments —
  // then groups client-side via groupRewardsByChild (reproduces the RPC's filter:
  // assigned-to-child OR no-assignments). Kills the /tabs/rewards N+1.
  async getRewardsForChildren(childIds: string[], householdId: string): Promise<Map<string, Reward[]>> {
    if (!childIds.length) return new Map()
    // Un seul enfant : c'est le cas des pages par enfant, ET le seul cas d'une
    // session ENFANT (son propre téléphone). Celle-ci ne lit aucune ligne de
    // `child_rewards`, dont la RLS ne s'ouvre qu'aux membres du foyer : sans
    // attribution visible, chaque récompense passait pour « à tout le monde »,
    // et l'enfant voyait en objectif celles de son frère ou de sa sœur, pendant
    // que son parcours (get_rewards_for_child) montrait les bonnes.
    // On garde les fiches complètes du foyer (`reward_kind` et
    // `allowance_amount` manquent à la RPC) et on les trie avec la RPC, qui
    // applique la règle d'attribution côté serveur, quelle que soit la session.
    if (childIds.length === 1) {
      const [allRewards, mine] = await Promise.all([
        this.getHouseholdRewards(householdId),
        this.getRewardsForChild(childIds[0]),
      ])
      const ids = new Set(mine.map(r => r.id))
      return new Map([[childIds[0], (allRewards as Reward[]).filter(r => ids.has(r.id))]])
    }
    const allRewards = await this.getHouseholdRewards(householdId)
    const { data: assignments, error } = await supabase
      .from('child_rewards')
      .select('reward_id, child_id')
      .in('child_id', childIds)
    if (error) throw error
    return groupRewardsByChild(allRewards as Reward[], (assignments ?? []) as Array<{ reward_id: string; child_id: string }>, childIds)
  },

  // Minimal columns for hot path (rewards list)
  _rewardSelectColumns: 'id,household_id,created_by,title,description,required_points,is_recurring,is_collective,category,image_url,expiration_date,availability_days,max_claims_per_child,parent_approval_required,icon,photo,reward_kind,allowance_amount,created_at,updated_at',

  // Get rewards by user (created by user)
  async getRewardsByUserId(userId: string): Promise<Reward[]> {
    const { data, error } = await supabase
      .from('rewards')
      .select(this._rewardSelectColumns)
      .eq('created_by', userId)
    if (error) throw error
    return (data || []).map((reward: any) => ({
      ...reward,
      user_id: reward.created_by || ''
    })) as Reward[]
  },

  // Create a new reward using direct Supabase API
  async createReward(reward: any): Promise<Reward> {
    const rewardData = {
      title: reward.title,
      description: reward.description,
      required_points: reward.required_points,
      category: reward.category,
      expiration_date: reward.expiration_date,
      is_recurring: reward.is_recurring,
      parent_approval_required: reward.parent_approval_required,
      max_claims_per_child: reward.max_claims_per_child,
      household_id: reward.household_id,
      created_by: reward.created_by,
      recurring_interval: reward.recurring_interval,
      max_occurrences: reward.max_occurrences,
      points_limit: reward.points_limit,
      recurring_start_date: reward.recurring_start_date,
      recurring_end_date: reward.recurring_end_date,
      icon: reward.icon || null,
      // Allowance ("argent de poche"): money-reward type + euro amount.
      // Columns exist in DB but aren't in the generated types yet → cast below.
      reward_kind: reward.reward_kind ?? 'classic',
      allowance_amount: reward.allowance_amount ?? 0,
      // Seulement si la récompense vient d'un modèle (HARMONIA-64), comme pour les tâches.
      ...(reward.template_id ? { template_id: reward.template_id } : {}),
      // La photo (#354), seulement si le parent en a mis une.
      ...(reward.photo ? { photo: reward.photo } : {}),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    } as any;

    const { data, error } = await supabase
      .from('rewards')
      .insert(rewardData)
      .select()
      .single();

    if (error) throw error
    return data as unknown as Reward
  },

  // Update a reward using direct Supabase API
  async updateReward(rewardId: string, update: any): Promise<Reward> {
    const updateData = {
      title: update.title,
      description: update.description,
      required_points: update.required_points,
      category: update.category,
      expiration_date: update.expiration_date,
      is_recurring: update.is_recurring,
      parent_approval_required: update.parent_approval_required,
      max_claims_per_child: update.max_claims_per_child,
      recurring_interval: update.recurring_interval,
      max_occurrences: update.max_occurrences,
      points_limit: update.points_limit,
      recurring_start_date: update.recurring_start_date,
      recurring_end_date: update.recurring_end_date,
      icon: update.icon || null,
      // Allowance ("argent de poche"): money-reward type + euro amount.
      // Columns exist in DB but aren't in the generated types yet → cast below.
      reward_kind: update.reward_kind ?? 'classic',
      allowance_amount: update.allowance_amount ?? 0,
      // La photo (#354) : seulement si l'appelant la fournit (null la retire).
      ...(update.photo !== undefined ? { photo: update.photo } : {}),
      updated_at: new Date().toISOString()
    } as any;

    const { data, error } = await supabase
      .from('rewards')
      .update(updateData)
      .eq('id', rewardId)
      .select()
      .single();

    if (error) throw error
    return data as unknown as Reward
  },

  // Delete a reward
  async deleteReward(rewardId: string): Promise<void> {
    const { error } = await supabase
      .from('rewards')
      .delete()
      .eq('id', rewardId)
    if (error) throw error
  },

  // Get collected rewards for a child using raw SQL
  async getCollectedRewards(childId: string): Promise<CollectedRewardRow[]> {
    const { data, error } = await supabaseRpc
      .rpc('get_collected_rewards_raw_sql', {
        p_child_id: childId
      })
    if (error) throw error
    return (data || []) as CollectedRewardRow[]
  },

  // Household-wide collected rewards, joined with child + reward, for the
  // parent-facing "À remettre" (pending to deliver) and "Recently delivered" views.
  async getHouseholdCollectedRewards(householdId: string): Promise<Array<CollectedRewardRow & {
    // `photo` (#354) n'est pas encore dans les types générés.
    reward: (Pick<RewardRow, 'id' | 'title' | 'required_points' | 'icon' | 'created_by'> & { photo?: string | null }) | null
    child: { id: string; name: string; color: string | null; avatar: string | null } | null
  }>> {
    const { data, error } = await supabase
      .from('collected_rewards')
      .select(`
        *,
        reward:rewards!reward_id(id, title, required_points, icon, photo, created_by),
        child:children!child_id(id, name, color, avatar)
      `)
      .eq('child.household_id', householdId)
      .neq('status', 'void')
      .order('created_at', { ascending: false })
    if (error) throw error
    // The join filter via child.household_id returns rows even when the join fails;
    // guard against stray rows whose child/reward are null.
    return (data || []).filter((row: any) => row.child && row.reward) as any
  },

  // Claim a collected reward
  async claimCollectedReward(collectedRewardId: string): Promise<void> {
    // `.select()` sert la mesure autant que le contrôle : la ligne renvoyée porte
    // l'enfant et la récompense, qu'aucun appelant ne connaît à ce stade (les
    // écrans ne passent qu'un identifiant d'instance collectée).
    const { data, error } = await supabase
      .from('collected_rewards')
      .update({ is_claimed: true, claimed_at: new Date().toISOString() })
      .eq('id', collectedRewardId)
      .select('reward_id, child_id')
      .maybeSingle()
    if (error) throw error

    // Le parent remet la récompense à l'enfant : c'est la fin réelle de la
    // boucle tâche → points → récompense, et elle n'était mesurée nulle part.
    if (data) analytics.logRewardRedeemed((data as any).reward_id, (data as any).child_id)
  },

  // Unclaim a collected reward
  async unclaimCollectedReward(collectedRewardId: string): Promise<boolean> {
    try {
      const { data, error } = await supabaseRpc.rpc('unclaim_collected_reward', {
        p_collected_reward_id: collectedRewardId
      });

      if (error) {
        console.error('Error in unclaim_collected_reward RPC:', error);
        return false;
      }

      return data || false;
    } catch (error) {
      console.error('Error in unclaimCollectedReward:', error);
      return false;
    }
  },

  // Delete a collected reward instance
  // Instead of actually deleting, mark it as "void" to prevent automatic re-assignment
  async deleteCollectedReward(collectedRewardId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('collected_rewards')
        .update({ status: 'void' })
        .eq('id', collectedRewardId);

      if (error) {
        console.error('Error marking collected reward as void:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteCollectedReward:', error);
      return false;
    }
  },

  // Collect the next instance of a recurring reward (when child has enough points).
  // Use this for the "Récupérer cette instance" button; assign_milestone_rewards may create nothing if counts already match.
  async collectNextRewardInstance(childId: string, rewardId: string, childPoints: number): Promise<{ id: string | null; error?: string }> {
    if (!childId?.trim() || !rewardId?.trim()) {
      return { id: null, error: 'childId and rewardId are required' };
    }
    if (typeof childPoints !== 'number' || childPoints < 0) {
      return { id: null, error: 'childPoints must be a non-negative number' };
    }
    try {
      const { data, error } = await supabaseRpc.rpc('collect_next_reward_instance', {
        p_child_id: childId,
        p_reward_id: rewardId,
        p_child_points: childPoints
      });
      if (error) {
        console.error('Error in collect_next_reward_instance RPC:', error);
        return { id: null, error: error.message };
      }
      return { id: data as string | null };
    } catch (err: any) {
      console.error('Error in collectNextRewardInstance:', err);
      return { id: null, error: err?.message };
    }
  },

  // Assign milestone rewards when a child reaches required points
  async assignMilestoneRewards(childId: string, householdId: string, childPoints: number): Promise<{ unlockedRewards: string[] } | void> {
    if (!childId?.trim() || !householdId?.trim()) {
      console.warn('assignMilestoneRewards: childId and householdId are required');
      return;
    }
    if (typeof childPoints !== 'number' || childPoints < 0) {
      console.warn('assignMilestoneRewards: childPoints must be a non-negative number');
      return;
    }
    try {
      const { data, error } = await supabaseRpc.rpc('assign_milestone_rewards', {
        p_child_id: childId,
        p_household_id: householdId,
        p_child_points: childPoints
      });

      if (error) {
        console.error('Error in assign_milestone_rewards RPC:', error);
        return;
      }

      return { unlockedRewards: data || [] };
    } catch (error) {
      console.error('Error in assignMilestoneRewards:', error);
      return;
    }
  },

  async setRewardAssignments(rewardId: string, childIds: string[]): Promise<void> {
    const payload = {
      p_reward_id: rewardId,
      p_child_ids: childIds && childIds.length > 0 ? childIds : null
    };

    const { error } = await supabaseRpc.rpc('set_reward_assignments', payload);

    if (error) throw error;
  }
}
